import { inject, Injectable } from "@angular/core";

import { DateTime } from "luxon";

import { Observable, of, switchMap } from "rxjs";

import { getContractualMonth } from "@shared/functions";
import { ActivityRecordResponse, ParsedSequence, RequestState } from "@shared/models";
import { ActivityRecordService } from "@shared/services";

@Injectable({
  providedIn: "root",
})
export class HomeService {
  activityRecordService = inject(ActivityRecordService);

  /**
   * Gets the current pilot sequence.
   * @param force Whether to force a refresh of the cache.
   * @returns An Observable of ParsedSequence.
   */
  getCurrentSequence(force: boolean, emualtedUser: string): Observable<ParsedSequence> {
    return this.activityRecordService
      .getActivityRecord(this.getDateParam(), force, emualtedUser)
      .pipe(switchMap((response) => this.processActivityRecordResponse(response)));
  }

  /**
   * Processes the response for activity records. If no valid sequence is found and response is not loading, it returns an empty sequence.
   * @param response The current month's activity record response.
   * @param secondMonth The ISO date string of the second month.
   * @param force Whether to force a refresh of the cache.
   * @returns An Observable of ParsedSequence.
   */
  private processActivityRecordResponse(response: RequestState<ActivityRecordResponse>): Observable<ParsedSequence> {
    if (response.isLoading) {
      return of(new ParsedSequence()); // Return an empty sequence if still loading
    }

    const currentSequence = this.handleResponse(response);
    if (currentSequence && currentSequence.sequence) {
      return of(currentSequence);
    }

    return of(new ParsedSequence({ noSequence: true }));
  }

  /**
   * Handles the response from activity record service and finds a valid sequence.
   * @param response The activity record response.
   * @returns ParsedSequence.
   */
  private handleResponse(response: RequestState<ActivityRecordResponse>): ParsedSequence {
    if (!response.isLoading && response.response) {
      const sequences = this.activityRecordService.parseActivityRecord(response.response);
      return this.findValidSequence(sequences);
    }
    return new ParsedSequence();
  }

  /**
   * Finds a valid sequence from the list of parsed sequences.
   * @param sequences List of ParsedSequence.
   * @returns ParsedSequence.
   */
  private findValidSequence(sequences: ParsedSequence[]): ParsedSequence {
    const currentDate = DateTime.utc().toISO().slice(0, 10);
    return sequences.find((sequence) => this.isSequenceValid(sequence, currentDate)) || new ParsedSequence();
  }

  /**
   * Determines if a sequence is valid based on current date.
   * @param sequence The sequence to check.
   * @param currentDate Current date in ISO format.
   * @returns boolean indicating if the sequence is valid.
   */
  private isSequenceValid(sequence: ParsedSequence, currentDate: string): boolean {
    const originateDate = this.formatDate(sequence.originate.date);
    const terminateDate = this.formatDate(sequence.terminate.date);
    return originateDate <= currentDate && terminateDate >= currentDate;
  }

  /**
   * Formats a date string from MM-DD-YY to YYYY-MM-DD.
   * @param date Date string in MM-DD-YY format.
   * @returns Formatted date string in YYYY-MM-DD format.
   */
  private formatDate(date: string): string {
    const [month, day, year] = date.split("-");
    return `20${year}-${month}-${day}`;
  }

  /**
   * Retrieves the ISO date strings for the current and next contractual months.
   * @returns Object containing firstMonth and secondMonth date strings.
   */
  private getDateParam(): string {
    const currentContractualMonth = getContractualMonth({ offset: 0 });
    const month = DateTime.local()
      .set({ month: Number(currentContractualMonth.start.month), day: Number(currentContractualMonth.start.day) })
      .toISODate();
    return month;
  }
}
